import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, TrendingUp, Calendar, Clock } from 'lucide-react';
import { formatRupees } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesTrends = ({ showToast }) => {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    loadTrends();
  }, []);

  const loadTrends = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sales-data/trends?months=6`);
      setTrends(response.data);
    } catch (error) {
      console.error('Failed to load trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCsvUpload = async () => {
    if (!csvContent) {
      showToast('Please enter CSV data', 'error');
      return;
    }

    try {
      const response = await axios.post(`${API}/sales-data/upload-csv`, { csv_content: csvContent });
      showToast(`${response.data.entries_added} entries uploaded successfully`, 'success');
      setCsvContent('');
      setShowUpload(false);
      loadTrends();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to upload CSV', 'error');
    }
  };

  const csvFormat = `CSV Format (Required columns):
date,time,brand_name,revenue,orders

Example:
2025-01-15,14:30,Wellcore,5000.50,25
2025-01-15,18:00,YouWeFit,3200.00,18
2025-01-16,12:00,Dynamite,4500.75,22`;

  if (loading) {
    return <div className="text-white">Loading trends...</div>;
  }

  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const sortedDayTrends = dayOrder
    .filter(day => trends?.day_of_week?.[day])
    .map(day => ({ day, data: trends.day_of_week[day] }));

  const sortedTimeTrends = Object.entries(trends?.time_of_day || {})
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([time, data]) => ({ time, data }));

  return (
    <div className="space-y-6">
      {/* Header with Upload Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white mb-1">📊 Sales Trends Analysis</h2>
          <p className="text-sm text-slate-400">Last 6 months data analysis</p>
        </div>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
        >
          <Upload size={18} />
          {showUpload ? 'Hide Upload' : 'Upload Data'}
        </button>
      </div>

      {/* CSV Upload Section */}
      {showUpload && (
        <div className="p-6 bg-slate-900 border border-indigo-500/30 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-3">Upload Sales Data</h3>
          
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <pre className="text-xs text-blue-300 whitespace-pre-wrap font-mono">{csvFormat}</pre>
          </div>

          <textarea
            value={csvContent}
            onChange={(e) => setCsvContent(e.target.value)}
            placeholder="Paste your CSV data here..."
            rows={8}
            className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleCsvUpload}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
            >
              Upload CSV
            </button>
            <button
              onClick={() => {
                setShowUpload(false);
                setCsvContent('');
              }}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Data Stats */}
      {trends && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-slate-800 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">Total Entries</p>
            <p className="text-2xl font-bold text-white">{trends.total_entries}</p>
          </div>
          <div className="p-4 bg-slate-800 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">Date Range</p>
            <p className="text-sm text-white">{trends.date_range?.start} to {trends.date_range?.end}</p>
          </div>
          <div className="p-4 bg-slate-800 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">Analysis Period</p>
            <p className="text-sm text-white">Last 6 Months</p>
          </div>
        </div>
      )}

      {/* Sales by Day of Week */}
      <div className="bg-slate-800 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="text-green-400" size={24} />
          <h3 className="text-lg font-semibold text-white">Sales Trends by Day of Week</h3>
        </div>

        {sortedDayTrends.length === 0 ? (
          <p className="text-slate-400 text-center py-8">No data available. Upload sales data to see trends.</p>
        ) : (
          <div className="space-y-3">
            {sortedDayTrends.map(({ day, data }) => {
              const maxRevenue = Math.max(...sortedDayTrends.map(d => d.data.avg_revenue));
              const percentage = (data.avg_revenue / maxRevenue) * 100;
              
              return (
                <div key={day} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-white w-24">{day}</span>
                    <div className="flex-1 mx-4">
                      <div className="h-8 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${percentage}%` }}
                        >
                          {percentage > 20 && (
                            <span className="text-xs font-semibold text-white">
                              {formatRupees(data.avg_revenue)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right w-32">
                      <p className="text-sm font-semibold text-white">{formatRupees(data.avg_revenue)}</p>
                      <p className="text-xs text-slate-400">{Math.round(data.avg_orders)} orders</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sales by Time of Day */}
      <div className="bg-slate-800 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Clock className="text-purple-400" size={24} />
          <h3 className="text-lg font-semibold text-white">Sales Trends by Time of Day</h3>
        </div>

        {sortedTimeTrends.length === 0 ? (
          <p className="text-slate-400 text-center py-8">No data available. Upload sales data to see trends.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {sortedTimeTrends.map(({ time, data }) => {
              const maxRevenue = Math.max(...sortedTimeTrends.map(t => t.data.avg_revenue));
              const percentage = (data.avg_revenue / maxRevenue) * 100;
              const isHighPerforming = percentage > 70;
              
              return (
                <div
                  key={time}
                  className={`p-4 rounded-lg border-2 ${
                    isHighPerforming
                      ? 'border-yellow-500 bg-yellow-500/10'
                      : 'border-slate-700 bg-slate-900'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-white">{time}</span>
                    {isHighPerforming && (
                      <span className="text-xs px-2 py-1 bg-yellow-500 text-black rounded font-bold">
                        🔥 HOT
                      </span>
                    )}
                  </div>
                  <p className="text-lg font-bold text-white mb-1">{formatRupees(data.avg_revenue)}</p>
                  <p className="text-xs text-slate-400">{Math.round(data.avg_orders)} orders avg</p>
                  <div className="mt-2 h-1 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Best Performing Times */}
      {sortedTimeTrends.length > 0 && (
        <div className="p-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-3">💡 Key Insights</h3>
          <div className="space-y-2 text-sm text-slate-300">
            <p>✓ Best performing day: <strong className="text-green-400">{sortedDayTrends[0]?.day}</strong></p>
            <p>✓ Peak sales hour: <strong className="text-green-400">
              {sortedTimeTrends.sort((a, b) => b.data.avg_revenue - a.data.avg_revenue)[0]?.time}
            </strong></p>
            <p>✓ Schedule campaigns during high-revenue time slots for maximum impact</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTrends;
